import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import type { MetricItem } from "../../Common/types";
import { status as statusKeys, WellnessScoreStatus } from "../../Common/types";
import { getBmiStatus } from "../BMI/BMIInfoCard/utils";
import {
  parseHealthProfileData,
  parseHealthProfileDataAsString,
} from "@/features/HealthScore/utils/parseHealthProfileData";

function getItemByType(
  items: HealthProfileSuccessResponseData["items"],
  questionType: string
) {
  return items.find((i) => i.questionType === questionType);
}

/** Family history: NO GREEN, YES RED. Count = number of conditions that are true (excluding NONE). */
function parseFamilyHistory(data: string | null | undefined): {
  count: number;
  hasAny: boolean;
} {
  const parsed = parseHealthProfileData(data);
  if (parsed == null || typeof parsed !== "object")
    return { count: 0, hasAny: false };
  const obj = parsed as Record<string, unknown>;
  if (obj.NONE === true) return { count: 0, hasAny: false };
  const conditions = [
    "DIABETES",
    "HIGH_BLOOD_PRESSURE",
    "CORONARY_ARTERY_DISEASE",
  ];
  let count = 0;
  for (const key of conditions) {
    if (obj[key] === true) count++;
  }
  return { count, hasAny: count > 0 };
}

/** Conditions: "0" GREEN, "1" AMBER, "2+" RED */
function parseConditions(data: string | null | undefined): {
  value: string;
  status: string;
  count: number;
} {
  const s = parseHealthProfileDataAsString(data);
  if (s == null || s === "")
    return { value: "—", status: statusKeys.needAttention, count: 0 };
  const normalized = s.replace(/\s/g, "").toUpperCase();
  if (normalized === "0" || normalized === "NONE")
    return { value: "0 conditions", status: statusKeys.healthy, count: 0 };
  if (normalized === "1" || normalized === "ONE")
    return { value: "1 condition", status: statusKeys.needAttention, count: 1 };
  return { value: "2+ conditions", status: statusKeys.needsSupport, count: 2 };
}

/** HivTb: NO GREEN, YES RED */
function parseHivTb(data: string | null | undefined): {
  value: string;
  status: string;
} {
  const s = parseHealthProfileDataAsString(data)?.toUpperCase();
  if (s == null || s === "")
    return { value: "—", status: statusKeys.needAttention };
  const isYes = s === "YES" || s === "TRUE";
  return {
    value: isYes ? "Yes" : "No",
    status: isYes ? statusKeys.needsSupport : statusKeys.healthy,
  };
}

/** Extracts a numeric value from a health profile item's data payload, handling both lowercase and uppercase keys. */
function parseNumericValue(data: string | null | undefined): number {
  const parsed = parseHealthProfileData(data);
  if (parsed == null) return 0;
  if (typeof parsed === "number") return Number.isFinite(parsed) ? parsed : 0;
  if (typeof parsed !== "object") return 0;
  const o = parsed as Record<string, unknown>;
  const v = Number(o.value ?? o.Value);
  return Number.isFinite(v) ? v : 0;
}

function getStatusKeyFromBmi(bmi: number): string {
  const status = getBmiStatus(bmi);
  if (status === WellnessScoreStatus.Healthy) return statusKeys.healthy;
  if (status === WellnessScoreStatus.NeedsAttention)
    return statusKeys.needAttention;
  return statusKeys.needsSupport;
}

export interface HealthHistoryFromProfile {
  familyHistoryCount: number;
  chronicDiseasesCount: number;
  hivTb: boolean;
  bmi: number;
  weightKg: number;
  heightCm: number;
  metrics: MetricItem[];
}

export function getHealthHistoryMetricsFromHealthProfile(
  healthProfile: HealthProfileSuccessResponseData | null,
  t: (key: string) => string
): HealthHistoryFromProfile | null {
  if (!healthProfile?.items?.length) return null;

  const items = healthProfile.items;
  const familyHistoryItem = getItemByType(items, "FamilyHistory");
  const conditionsItem = getItemByType(items, "Conditions");
  const hivTbItem = getItemByType(items, "HivTb");
  const bmiItem = getItemByType(items, "BodyMassIndex");
  const weightItem = getItemByType(items, "Weight");
  const heightItem = getItemByType(items, "Height");

  const familyHistory = parseFamilyHistory(familyHistoryItem?.data?.value);
  const familyHistoryCount = familyHistory.hasAny ? familyHistory.count : 0;
  const familyHistoryStatus = familyHistory.hasAny
    ? statusKeys.needsSupport
    : statusKeys.healthy;
  const familyHistoryValue = familyHistory.hasAny
    ? `${familyHistory.count} ${familyHistory.count === 1 ? "condition" : "conditions"}`
    : "No";

  const conditions = parseConditions(conditionsItem?.data?.value);
  const chronicDiseasesCount = conditions.count;
  const hivTbParsed = parseHivTb(hivTbItem?.data?.value);
  const hivTb = hivTbParsed.value === "Yes";

  const bmi = parseNumericValue(bmiItem?.data?.value);
  const weightKg = parseNumericValue(weightItem?.data?.value);

  let heightCm = parseNumericValue(heightItem?.data?.value);
  const heightParsed = parseHealthProfileData(heightItem?.data?.value);
  if (heightParsed != null && typeof heightParsed === "object") {
    const o = heightParsed as Record<string, unknown>;
    const unit = String(o.unit ?? o.Unit ?? "").toUpperCase();
    if (unit === "M") heightCm = Math.round(heightCm * 100);
  }

  const bmiStatusKey = getStatusKeyFromBmi(bmi);
  let bmiDisplay: string;
  if (bmi % 1 === 0) {
    bmiDisplay = String(Math.round(bmi));
  } else {
    bmiDisplay = bmi.toFixed(2);
  }

  const metrics: MetricItem[] = [
    {
      id: "familyHistory",
      title: t("HealthHistory.Category.metrics.familyHistory"),
      value: familyHistoryValue,
      status: familyHistoryStatus,
      showChevron: false,
    },
    {
      id: "chronicDiseases",
      title: t("HealthHistory.Category.metrics.chronicDiseases"),
      value: conditions.value,
      status: conditions.status,
      showChevron: false,
    },
    {
      id: "hivTb",
      title: t("HealthHistory.Category.metrics.hivTb"),
      value: hivTbParsed.value,
      status: hivTbParsed.status,
      showChevron: false,
    },
    {
      id: "bmi",
      title: t("HealthHistory.Category.metrics.bmi"),
      value: bmiDisplay,
      status: bmiStatusKey,
      showChevron: true,
    },
  ];

  return {
    familyHistoryCount,
    chronicDiseasesCount,
    hivTb,
    bmi,
    weightKg,
    heightCm,
    metrics,
  };
}
